import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CircleCheck, Hourglass, FlaskConical, Loader2, RotateCcw, Store, Building2, Play } from 'lucide-react';
import { fill, demoInfo, daysLeft, PACK_PRICE, enterpriseLicense, playLinks } from '../lib/license';
import { PLAY_PRODUCTS, playService, playPrices, playBuy, playRestore } from '../lib/playBilling';

const fmtDate = (ms, t) => new Date(ms).toLocaleDateString(t.locale, { day: '2-digit', month: '2-digit', year: 'numeric' });

// main unlock: Play Billing inside the Play Store app, otherwise open the Play Store link
const askMove = (t) => async () => window.confirm(t.playBoundQ);
const playMsg = (t, e, fb) => ({ play_bound: t.playBound, play_switch_limit: t.playSwitchLimit }[e?.message] || fb);

export function PlayUnlockBtn({ t, onChange, testid = 'play-unlock-btn', small }) {
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState('');
  const sku = PLAY_PRODUCTS[0].sku;
  useEffect(() => { playPrices().then((p) => setPrice(p[sku] || '')); }, [sku]);
  const go = async () => {
    if (!(await playService())) { window.open(playLinks().store, '_blank', 'noopener'); return; }
    setBusy(true);
    try { await playBuy(sku, askMove(t)); toast.success(t.playOk); onChange?.(); } catch (e) { if (e?.name !== 'AbortError') toast.error(playMsg(t, e, t.playErr), { duration: 8000 }); }
    setBusy(false);
  };
  return (
    <button className={`btn btn-play ${small ? 'btn-sm' : ''}`} data-testid={testid} disabled={busy} onClick={go}>
      {busy ? <Loader2 size={16} className="spin" /> : <Play size={16} fill="currentColor" />} {t.playUnlock}
      <small>{price || `${PACK_PRICE.ALL.toFixed(2).replace('.', ',')} € ${t.plusVat}`} / {t.playYear} · {t.playTrial7}</small>
    </button>
  );
}

export function PlayStore({ t, onChange }) {
  const [avail, setAvail] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { playService().then((s) => setAvail(!!s)); }, []);
  const restore = async () => {
    setBusy(true);
    try { const r = await playRestore(askMove(t)); toast[r.length ? 'success' : 'message'](r.length ? t.playRestored : t.playNothing); onChange(); } catch (e) { toast.error(playMsg(t, e, t.playOnly), { duration: 8000 }); }
    setBusy(false);
  };
  return (
    <div className="buy-box" data-testid="play-box">
      <p className="muted small-hint" data-testid="license-allinone-hint">{t.licAllInOne}</p>
      <PlayUnlockBtn t={t} onChange={onChange} />
      <button className="btn btn-outline" data-testid="play-restore-btn" disabled={!avail || busy} onClick={restore}>
        {busy ? <Loader2 size={16} className="spin" /> : <RotateCcw size={16} />} {t.playRestore}
      </button>
      {avail === false && <p className="muted small-hint" data-testid="play-unavailable-hint"><Store size={13} /> {t.playWebHint}</p>}
    </div>
  );
}

export function LicenseStatus({ t, lics }) {
  const demo = demoInfo();
  const best = {};
  lics.forEach((l) => { const b = best.all; if (!b || (b.exp != null && (l.exp == null || l.exp > b.exp))) best.all = l; });
  const rows = Object.entries(best).map(([m, l]) => ({ l, m }));
  const ent = enterpriseLicense();
  return (
    <div className="lic-status" data-testid="license-active-list">
      {ent && <p className="lic-line ok" data-testid="license-enterprise"><Building2 size={15} /> <b>{t.entActive}:</b> {ent.ent || ''} · {fill(t.licActiveUntil, { date: fmtDate(ent.exp, t), days: daysLeft(ent.exp) })}</p>}
      {!rows.length && demo.active && <p className="lic-line demo" data-testid="license-demo-active"><FlaskConical size={15} /> {fill(t.demoActive, { days: demo.daysLeft })}</p>}
      {!rows.length && !demo.active && <p className="lic-line bad" data-testid="license-demo-over"><Hourglass size={15} /> {t.demoOver}</p>}
      {rows.map(({ l, m }) => (
        <p key={l.code + m} className={`lic-line ${l.expired ? 'bad' : 'ok'}`} data-testid={`license-module-${m}`}>
          {l.expired ? <Hourglass size={15} /> : <CircleCheck size={15} />}
          <b>{t['mod_' + m]}:</b>{' '}
          {l.exp == null ? t.unlimited
            : l.expired && String(l.code).startsWith('GISS-ENT-') ? fill(t.entExpired, { date: fmtDate(l.exp, t) })
            : l.expired ? fill(t.licYearExpired, { price: PACK_PRICE.ALL.toFixed(2).replace('.', ','), date: fmtDate(l.exp, t) })
              : fill(t.licActiveUntil, { date: fmtDate(l.exp, t), days: daysLeft(l.exp) })}
        </p>
      ))}
    </div>
  );
}
